import styled from "styled-components";
import { FadeImage } from "../../general/FadeImage";
import {
  colors,
  easing,
  fonts,
  layout,
  media,
  radii,
  shadows,
} from "../../../theme";

const MOBILE_TOC_HEIGHT = "3.75rem";

export const ArticleLayout = styled.div`
  display: grid;
  grid-template-columns: 15rem minmax(0, 1fr);
  gap: clamp(2.5rem, 6vw, 5rem);
  width: 100%;
  max-width: ${layout.maxWidth};
  margin: 0 auto;
  padding: clamp(3rem, 6vw, 5rem) ${layout.gutter};

  ${media.tablet} {
    display: block;
    padding-top: 0;
  }
`;

export const ArticleSidebar = styled.aside`
  position: sticky;
  top: calc(${layout.navHeightCompact} + 2rem);
  align-self: start;

  ${media.tablet} {
    top: ${layout.navHeightCompact};
    z-index: 15;
    margin: 0 calc(-1 * ${layout.gutter}) 2.5rem;
    border-bottom: 1px solid rgba(210, 180, 140, 0.35);
    background-color: rgba(255, 255, 234, 0.9);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
`;

export const TocLabel = styled.p`
  margin-bottom: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.captions};

  ${media.tablet} {
    display: none;
  }
`;

export const TocList = styled.ul`
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;

  ${media.tablet} {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: ${MOBILE_TOC_HEIGHT};
    padding: 0 ${layout.gutter};
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const TocNumber = styled.span`
  font-family: ${fonts.heading};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${colors.border};
  transition: color 0.3s ease;
`;

export const TocButton = styled.button`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  width: 100%;
  padding: 0.65rem 0 0.65rem 1rem;
  border: none;
  border-left: 2px solid
    ${({ $active }) => ($active ? colors.primary : "rgba(210, 180, 140, 0.45)")};
  background: none;
  font-size: 0.95rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  line-height: 1.4;
  text-align: left;
  color: ${({ $active }) => ($active ? colors.textMain : colors.textMuted)};
  cursor: pointer;
  transition: color 0.3s ease, border-color 0.3s ease,
    padding-left 0.4s ${easing.out};

  ${TocNumber} {
    color: ${({ $active }) => ($active ? colors.buttonHover : colors.border)};
  }

  &:hover {
    padding-left: 1.25rem;
    color: ${colors.textMain};
  }

  ${media.tablet} {
    width: auto;
    padding: 0.45rem 0.95rem;
    border: 1px solid
      ${({ $active }) =>
        $active ? colors.secondary : "rgba(210, 180, 140, 0.8)"};
    border-radius: ${radii.pill};
    background-color: ${({ $active }) =>
      $active ? colors.secondary : colors.backgroundSecondary};
    color: ${({ $active }) => ($active ? colors.primary : colors.textMain)};
    font-size: 0.85rem;
    white-space: nowrap;
    transition: color 0.3s ease, border-color 0.3s ease,
      background-color 0.3s ease;

    ${TocNumber} {
      display: none;
    }

    &:hover {
      padding-left: 0.95rem;
    }
  }
`;

export const ArticleContent = styled.div`
  max-width: 46rem;
`;

export const ArticleSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: clamp(3rem, 6vw, 4.5rem);
  scroll-margin-top: calc(${layout.navHeightCompact} + 2rem);

  & + & {
    padding-top: clamp(3rem, 6vw, 4.5rem);
    border-top: 1px solid rgba(210, 180, 140, 0.4);
  }

  ${media.tablet} {
    scroll-margin-top: calc(
      ${layout.navHeightCompact} + ${MOBILE_TOC_HEIGHT} + 1.5rem
    );
  }
`;

export const SectionNumber = styled.span`
  font-family: ${fonts.heading};
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: ${colors.buttonHover};
`;

export const SectionTitle = styled.h2`
  margin-top: 0.35rem;
  font-size: clamp(1.75rem, 3.2vw, 2.4rem);
  color: ${colors.textMain};
`;

export const ArticleSubtitle = styled.h3`
  font-size: 1.4rem;
`;

export const ArticleParagraph = styled.p`
  font-size: 1.0625rem;
  line-height: 1.8;
  color: ${colors.textMuted};
`;

export const ArticleList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin: 0;
  padding: 1.25rem 1.5rem;
  border-radius: ${radii.md};
  background-color: ${colors.backgroundTernary};
  list-style: none;

  li {
    position: relative;
    padding-left: 1.6rem;
    font-weight: 500;
    color: ${colors.textMain};
  }

  li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.45em;
    width: 0.7rem;
    height: 0.8rem;
    background-color: ${colors.primary};
    clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%);
  }
`;

export const ArticleFigure = styled.figure`
  margin: 0.5rem 0;
`;

export const ArticleImage = styled(FadeImage)`
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: 70vh;
  margin: 0 auto;
  border-radius: ${radii.lg};
  box-shadow: ${shadows.md};
`;
